// Regole della home del cliente (bozza design/home-cliente-v8.html): fase del percorso, cosa va nella card
// "Oggi", quali visite aspettano l'esito, prossimo appuntamento, etichette degli immobili, novità, testi.
// La regola delle fasi e del prossimo appuntamento è la stessa del server (calcola_percorso in server.py):
// le due si provano con gli stessi casi (casi-percorso.json). La pagina la calcola da sé con i dati che
// ha già, così funziona anche con un backend che non la restituisce ancora.
//
// Date: visite e appuntamenti sono "ora di Roma senza fuso" e new Date(testo) le legge come ora locale (per questo
// "adesso" si calcola con adessoRoma); cronologia, registrazione ed esiti sono salvati in UTC senza fuso e vanno
// letti con leggiDataUtc.
import { quandoVisita, conAnno } from './visite';
import { ilGiorno } from './agenda';
import { bcpDi, contesto, dataIn, PRINCIPALE } from '../i18n';

export { ilGiorno, conAnno };

// Lingua del cliente (i18n.js): le funzioni che scrivono testo la prendono come ultimo parametro, con l'italiano
// come valore di base. Così le pagine dello staff, che non la passano, restano in italiano esattamente com'erano.
// Le date di queste pagine sono già Date con i campi dell'ora di Roma (adessoRoma, leggiDataUtcRoma): si scrivono
// con il fuso del browser (timeZone spento), altrimenti Intl le sposterebbe una seconda volta.
const scritta = (lingua, d, opzioni) => dataIn(lingua, d, { ...opzioni, timeZone: undefined });
// La stessa data con l'anno quando è di un altro anno rispetto ad "adesso" (come conAnno per l'italiano)
const scrittaConAnno = (lingua, d, adesso, opzioni) => scritta(lingua, d, {
  ...opzioni, ...(adesso && d.getFullYear() !== adesso.getFullYear() ? { year: 'numeric' } : {}),
});

export const GIORNI_VALIDITA_VISITA = 30;
export const STATI_PROPOSTA_CHIUSI = ['rifiutata', 'ritirata'];
export const MINUTI_RICHIAMO_ESITO = 120; // il promemoria "Com'è andata?" parte 2 ore dopo la visita

export const FASI = [
  { numero: 1, chiave: 'ricerca', nome: 'Ricerca', articolo: 'la', prossimoPasso: 'la visita',
    titolo: 'iniziamo a cercare.', dopo: 'quando troviamo la casa giusta, organizziamo la visita.' },
  { numero: 2, chiave: 'visita', nome: 'Visita', articolo: 'la', prossimoPasso: 'la proposta',
    titolo: 'ci siamo quasi.',
    dopo: 'se vi convince, prepariamo la proposta con la delibera già in mano; se non fa per voi, torniamo alla ricerca con un criterio in più.' },
  { numero: 3, chiave: 'proposta', nome: 'Proposta', articolo: 'la', prossimoPasso: 'il mutuo',
    titolo: 'la trattativa è aperta.', dopo: "quando l'offerta è accettata, chiudiamo il mutuo." },
  { numero: 4, chiave: 'mutuo', nome: 'Mutuo', articolo: 'il', prossimoPasso: 'il rogito',
    titolo: 'manca poco al rogito.', dopo: 'con la delibera, fissiamo il rogito dal notaio.' },
  { numero: 5, chiave: 'rogito', nome: 'Rogito', articolo: 'il', prossimoPasso: 'le chiavi',
    titolo: 'è quasi casa vostra.', dopo: 'la consegna delle chiavi.' },
];

// Perché una visita è andata male: chiavi e ordine uguali a backend/motivi_visita.py (un test lo verifica).
// Le chiavi vecchie non si toccano — sono già scritte nelle visite fatte — cambiano le parole, e tre motivi sono
// nuovi: la casa non più disponibile e i due «non si è presentato».
// Queste parole sono anche la RISERVA: se la traduzione manca o arriva vuota, si mostra quella italiana di qui,
// così l'elenco non è mai vuoto (era proprio un elenco vuoto a bloccare l'esito, 09/10/2026).
export const MOTIVI = [
  ['prezzo', 'Prezzo troppo alto per quello che offre'],
  ['zona', 'Zona non adatta'],
  ['stato', "Condizioni dell'immobile (da ristrutturare, umidità, impianti)"],
  ['metratura', 'Spazi o disposizione non adatti'],
  ['piano', 'Piano, ascensore o accessibilità'],
  ['luce', 'Luce, rumore o esposizione'],
  ['spese', 'Spese condominiali o costi troppo alti'],
  ['non_disponibile', 'Non più disponibile (venduta, in trattativa)'],
  ['cliente_assente', 'Il cliente non si è presentato'],
  ['agenzia_assente', "L'agenzia non si è presentata o ha annullato"],
  ['altro', 'Altro'],
];
// Chi non si presenta lo segna solo lo staff: al cliente non si chiede se è venuto, e l'agenzia è una cosa nostra.
export const MOTIVI_SOLO_STAFF = ['cliente_assente', 'agenzia_assente'];
// «Altro» da solo non dice niente: con questo scelto le due parole diventano obbligatorie.
export const MOTIVO_CON_NOTA = 'altro';
// L'elenco per lo staff, sempre pieno: è una costante, non si carica da nessuna parte e non può fallire.
export const motiviVisitaStaff = () => MOTIVI;

const GIORNI = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];
const MESI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
const MESI_BREVI = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
const GIORNO_MS = 24 * 60 * 60 * 1000;

// ---------------------------------------------------------------- date

export const leggiData = (valore) => {
  if (!valore) return null;
  const d = new Date(valore);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const leggiDataUtc = (valore) => {
  if (!valore) return null;
  const testo = String(valore);
  return leggiData(/(Z|[+-]\d\d:?\d\d)$/.test(testo) ? testo : `${testo}Z`);
};

// "Adesso" nell'ora di Roma. Visite e appuntamenti arrivano come ora di Roma senza fuso e new Date(testo) li legge
// come ora locale: con il browser su un altro fuso (un cliente a Londra, un telefono in viaggio) il confronto con
// new Date() sbagliava di una o più ore (visita già passata o ancora in programma all'ora sbagliata). Qui un Date
// i cui campi locali (giorno, ore, minuti) sono quelli di Roma in quell'istante: si confronta alla pari con le
// date delle visite. Sul fuso di Roma è uguale a new Date().
const FORMATO_ROMA = (() => {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Rome', year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23',
    });
  } catch { return null; }
})();
export const oraDiRoma = (istante = new Date()) => {
  if (!FORMATO_ROMA) return new Date(istante);
  const p = {};
  FORMATO_ROMA.formatToParts(istante).forEach(({ type, value }) => { p[type] = Number(value); });
  return new Date(p.year, p.month - 1, p.day, p.hour % 24, p.minute, p.second, istante.getMilliseconds());
};
export const adessoRoma = () => oraDiRoma(new Date());

// Date salvate in UTC (cronologia e Novità, note dello staff, registrazione, documenti, firme: arrivano con la Z o
// senza fuso) mostrate nell'ora di Roma, anche con il telefono su un altro fuso (prima: nell'ora del telefono, quindi
// "Oggi · 03:12" a New York per un fatto delle 09:12 di Roma, o il giorno dopo a Tokyo). Non per le date delle
// visite e degli appuntamenti, che sono già l'ora di Roma senza fuso (quelle si leggono con leggiData).
// - leggiDataUtcRoma(valore): un Date con i campi locali di Roma (per giornoEMese, ora, quandoGiorno… e per i
//   confronti con adessoRoma); accetta il testo del server o un Date vero
// - formattaRoma(valore, opzioni, lingua): il testo con Intl.DateTimeFormat(lingua, {timeZone: 'Europe/Rome', ...opzioni});
//   valore = testo del server in UTC o un Date vero (non uno già passato da oraDiRoma). Senza opzioni "15 set 2026, 09:12",
//   senza lingua l'italiano (le pagine dello staff); con la lingua del cliente "19 вер" invece di "19 set".
// - giornoDiRoma(valore): il giorno di Roma "AAAA-MM-GG"
const valido = (d) => (d instanceof Date && !Number.isNaN(d.getTime()) ? d : null);
const istanteUtc = (valore) => (valore instanceof Date ? valido(valore) : leggiDataUtc(valore));
export const leggiDataUtcRoma = (valore) => {
  const d = istanteUtc(valore);
  return d ? oraDiRoma(d) : null;
};
const FORMATO_BASE_ROMA = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };
export const formattaRoma = (valore, opzioni = FORMATO_BASE_ROMA, lingua = PRINCIPALE) => {
  const d = istanteUtc(valore);
  if (!d) return '';
  const bcp = bcpDi(lingua);   // senza lingua resta l'italiano: le pagine dello staff non cambiano
  try {
    return new Intl.DateTimeFormat(bcp, { timeZone: 'Europe/Rome', ...opzioni }).format(d);
  } catch {
    return d.toLocaleString(bcp, opzioni); // browser senza i fusi di Intl: l'ora del telefono
  }
};
export const giornoDiRoma = (valore) => {
  const d = leggiDataUtcRoma(valore);
  return d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : null;
};
// La disponibilità per il testo "aggiornata da voi il 15 set" (testoAggiornata di utils/agenda.js, che legge
// aggiornata_alle in UTC con l'ora del telefono): il giorno di Roma al posto dell'istante
export const disponibilitaGiornoDiRoma = (disp) => {
  const giorno = giornoDiRoma(disp?.aggiornata_alle);
  return giorno ? { ...disp, aggiornata_alle: null, aggiornata_il: giorno } : disp;
};

const inizioGiorno = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const domaniDi = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
const stessoGiorno = (a, b) => a && b && inizioGiorno(a).getTime() === inizioGiorno(b).getTime();
const giorniTra = (da, a) => Math.round((inizioGiorno(a) - inizioGiorno(da)) / GIORNO_MS);
const due = (n) => String(n).padStart(2, '0');

export const ora = (d) => `${due(d.getHours())}:${due(d.getMinutes())}`;
// Le date scritte senza orario (la perizia della banca si segna solo col giorno) arrivano a mezzanotte
export const senzaOrario = (d) => d.getHours() === 0 && d.getMinutes() === 0;
// " alle 10:30", o niente per un appuntamento senza orario (in russo " в 10:30": la chiave con {quando} vuoto)
export const alleOra = (d, lingua = PRINCIPALE) => (senzaOrario(d) ? ''
  : contesto(lingua).t('home.riepilogo.alle_ore', { quando: '', ora: ora(d) }));
export const nomeGiorno = (d, lingua = PRINCIPALE) => (lingua === PRINCIPALE ? GIORNI[d.getDay()]
  : scritta(lingua, d, { weekday: 'long' }));
export const giornoEMese = (d, lingua = PRINCIPALE) => (lingua === PRINCIPALE ? `${d.getDate()} ${MESI[d.getMonth()]}`
  : scritta(lingua, d, { day: 'numeric', month: 'long' }));
// "mercoledì 20 gennaio", con l'anno quando è diverso da quello di adesso: "mercoledì 20 gennaio 2027" (conAnno,
// la stessa dei punti sistemati al giro 8). Prima le date dell'anno prossimo — la card del prossimo appuntamento,
// "Rogito venerdì 26 febbraio", la scadenza della proposta — sembravano di quest'anno, mentre quelle passate l'anno
// ce l'avevano già.
export const dataLunga = (d, adesso = new Date(), lingua = PRINCIPALE) => (lingua === PRINCIPALE
  ? conAnno(`${nomeGiorno(d)} ${giornoEMese(d)}`, d, adesso)
  : scrittaConAnno(lingua, d, adesso, { weekday: 'long', day: 'numeric', month: 'long' }));
const maiuscola = (testo) => (testo ? testo.charAt(0).toUpperCase() + testo.slice(1) : testo);

// "oggi", "domani", "sabato" (entro la settimana), "sabato 26 settembre"
export const quandoGiorno = (d, adesso, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  const tra = giorniTra(adesso, d);
  if (tra === 0) return t('calendario.oggi');
  if (tra === 1) return t('calendario.domani');
  if (tra === -1) return t('calendario.ieri');
  if (tra > 1 && tra < 7) return nomeGiorno(d, lingua);
  return dataLunga(d, adesso, lingua);
};

// Orario delle novità: "Oggi · 09:12", "Ieri · 16:05", "Martedì · 11:30", "3 set · 11:30"
export const quandoNovita = (d, adesso, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  const fa = giorniTra(d, adesso);
  let giorno;
  if (fa <= 0) giorno = t('comune.oggi');
  else if (fa === 1) giorno = t('comune.ieri');
  else if (fa < 7) giorno = maiuscola(nomeGiorno(d, lingua));
  else if (lingua === PRINCIPALE) giorno = `${d.getDate()} ${MESI_BREVI[d.getMonth()]}${d.getFullYear() !== adesso.getFullYear() ? ` ${d.getFullYear()}` : ''}`;
  else giorno = scrittaConAnno(lingua, d, adesso, { day: 'numeric', month: 'short' });
  return `${giorno} · ${ora(d)}`;
};

// "tra 25 minuti", "tra un'ora", "tra 3 ore"
export const traQuanto = (d, adesso, lingua = PRINCIPALE) => {
  const { t, tp } = contesto(lingua);
  const minuti = Math.max(0, Math.round((d - adesso) / 60000));
  if (minuti < 1) return t('comune.adesso');
  if (minuti < 60) return tp('comune.plurali.tra_minuti', minuti);
  return tp('comune.plurali.tra_ore', Math.round(minuti / 60));
};

// ---------------------------------------------------------------- fase e appuntamenti (come il server)

export const visitaInSospeso = (visita, adesso) => {
  if (visita.esito_visita) return false;
  const data = leggiData(visita.data_visita);
  return !!data && data >= new Date(adesso.getTime() - GIORNI_VALIDITA_VISITA * GIORNO_MS);
};

const MUTUO_CONCLUSO = ['delibera', 'erogato'];

// Mutuo chiesto alla banca e non ancora deliberato (né erogato, né rifiutato): come mutuo_in_corso del server
export const mutuoInCorso = (p) => {
  if (MUTUO_CONCLUSO.includes(p.mutuo_stato) || p.mutuo_stato === 'rifiutato') return false;
  return Boolean(p.mutuo_richiesto) || ['richiesto', 'perizia'].includes(p.mutuo_stato);
};

// "Proposta non accettata": le proposte per la casa sono tutte chiuse (almeno una rifiutata o ritirata, nessuna aperta
// né accettata), anche se lo stato dello staff dice ancora "proposta in corso" (la risposta del venditore non lo
// cambia); non con il rogito in corso. Stessa regola del server (proposte_non_accettate). Prima, dopo il rifiuto del
// venditore, la casa restava "Proposta in corso", con la rata, «Prenotate una seconda visita» e la fase Proposta.
export const propostaNonAccettata = (immobile, proposte) => {
  const sue = (proposte || []).filter((p) => p.immobile_id === immobile.id);
  return immobile.stato_attivita !== 'rogito_in_corso' && sue.length > 0 && sue.every((p) => STATI_PROPOSTA_CHIUSI.includes(p.stato));
};

// La data del rogito è solo prevista (il giorno, senza ora): con il mutuo ancora in banca, o rifiutato dalla banca
// (prima, con il mutuo rifiutato: "Fase 5 di 5 · Rogito" e "Rogito dal notaio domenica 25 ottobre alle 10:00").
// Stessa regola del server (rogito_solo_previsto).
export const rogitoSoloPrevisto = (p) => mutuoInCorso(p) || p.mutuo_stato === 'rifiutato';

// ---------------------------------------------------------------- dove sta una casa (etichetta, fase, server)

// Quando la casa è uscita per la proposta non accettata: la risposta più recente (senza, la data della proposta; senza
// nessuna delle due, adesso). Date in UTC lette nell'ora di Roma, come le visite.
const dataRifiuto = (sue, adesso) => sue
  .map((p) => leggiDataUtcRoma(p.data_risposta) || leggiDataUtcRoma(p.data_proposta) || adesso)
  .reduce((a, b) => (b > a ? b : a));
// Quando il cliente ha dato l'esito (data_esito); nei dati di prima, l'ora della visita
const momentoEsito = (v) => leggiDataUtcRoma(v.data_esito) || leggiData(v.data_visita);
const recenteVisita = (v, adesso) => leggiData(v.data_visita) >= new Date(adesso.getTime() - GIORNI_VALIDITA_VISITA * GIORNO_MS);

// Le situazioni che tengono la casa fuori dalla ricerca (etichetta grigia, filtro "Scartati")
export const SITUAZIONI_FUORI = ['scartato', 'non_accettata', 'non_fa_per_voi'];

// Dove sta una casa per il cliente: una regola sola per l'etichetta, la fase del percorso e il server
// (situazione_immobile in server.py, provata con gli stessi casi). → { chiave, ultimo, recente, futura }
// chiave: scartato, accettata, trattativa, visita (in programma, in attesa di "Com'è andata?" o del nuovo orario;
// futura = la prossima), non_accettata, richiesta, non_fa_per_voi, preparazione, piaciuta (oltre i 30 giorni), altro.
// - Proposta non accettata (proposte tutte chiuse): contano solo le visite fissate dopo il rifiuto; una visita nuova in
//   programma o in attesa d'esito rimette in gioco la casa (prima: "Tolta dalla ricerca" con sotto la visita e Sposta).
// - Rimessa in gioco dallo staff (rimessa_in_gioco_il: un cambio di stato dopo il rifiuto o l'esito negativo): non è
//   più "Proposta non accettata" né "Non fa per voi" (prima: email "si può visitare" e la richiesta di visita a 400).
export const situazioneImmobile = (immobile, visite, proposte, adesso) => {
  const stato = immobile.stato_attivita || 'nuovo';
  const vuota = { ultimo: null, recente: false, futura: null };
  if (stato === 'scartato') return { chiave: 'scartato', ...vuota };
  const sue = (proposte || []).filter((p) => p.immobile_id === immobile.id);
  const accettata = sue.find((p) => p.stato === 'accettata');
  // "Offerta accettata" solo se c'è davvero una proposta accettata (o se non ne è registrata nessuna e lo stato dice
  // rogito): con la proposta accettata eliminata dallo staff e una rifiutata rimasta sulla stessa casa, il solo stato
  // "rogito_in_corso" teneva il cliente su "Offerta accettata" e "Fase 4 di 5 · Mutuo" per sempre
  if (accettata || (stato === 'rogito_in_corso' && !sue.length)) return { chiave: 'accettata', ...vuota, proposta: accettata || null };
  const chiuse = sue.length > 0 && sue.every((p) => STATI_PROPOSTA_CHIUSI.includes(p.stato));
  const rifiuto = chiuse ? dataRifiuto(sue, adesso) : null;
  const rimessa = leggiDataUtcRoma(immobile.rimessa_in_gioco_il);
  const rimessaDopo = (d) => !!rimessa && !!d && rimessa > d;
  if ((sue.length && !chiuse) || (stato === 'proposta_in_corso' && (!chiuse || rimessaDopo(rifiuto)))) return { chiave: 'trattativa', ...vuota };
  const sueVisite = (visite || []).filter((v) => v.immobile_id === immobile.id && !v.annullata && leggiData(v.data_visita)
    && (!rifiuto || leggiData(v.data_visita) > rifiuto));
  const ultimo = sueVisite.filter((v) => ['positivo', 'negativo'].includes(v.esito_visita))
    .reduce((a, b) => (!a || leggiData(b.data_visita) > leggiData(a.data_visita) ? b : a), null);
  const base = { ultimo, recente: !!ultimo && recenteVisita(ultimo, adesso), futura: null };
  const inAttesa = sueVisite.filter((v) => !v.esito_visita).sort((a, b) => leggiData(a.data_visita) - leggiData(b.data_visita));
  const futura = inAttesa.find((v) => leggiData(v.data_visita) > adesso) || null;
  const nuovoOrario = inAttesa.some((v) => attendeNuovoOrario(v, adesso));
  if (futura || nuovoOrario || inAttesa.some((v) => chiedeEsito(v, adesso))) return { chiave: 'visita', ...base, futura, nuovoOrario };
  if (rifiuto && !ultimo && !rimessaDopo(rifiuto)) return { chiave: 'non_accettata', ...base };
  if (immobile.visita_richiesta || stato === 'visita_richiesta') return { chiave: 'richiesta', ...base };
  if (ultimo?.esito_visita === 'negativo' && !rimessaDopo(momentoEsito(ultimo))) return { chiave: 'non_fa_per_voi', ...base };
  if (ultimo?.esito_visita === 'positivo') return { chiave: base.recente ? 'preparazione' : 'piaciuta', ...base };
  return { chiave: 'altro', ...base };
};

// "Proposta in preparazione" per la fase: in gioco, con l'ultimo esito che conta positivo ed entro i 30 giorni (anche
// con una seconda visita o una richiesta nuova in corso). Stessa regola del server (in_preparazione).
export const inPreparazione = (s) => ['visita', 'richiesta', 'preparazione'].includes(s.chiave)
  && s.ultimo?.esito_visita === 'positivo' && s.recente;

// Con il mutuo ancora in banca (o rifiutato) si resta nella fase Mutuo anche se c'è già una data prevista del rogito.
// Proposta (decisione del giro 7): una proposta aperta, una casa in trattativa o almeno una casa in gioco "Proposta in
// preparazione", con la regola dell'etichetta. Prima contava solo l'ultima visita con esito di tutte le case: "Ci
// piace" a via Marconi e poi "Non fa per noi" a via Inglesina riportavano alla Ricerca ("iniziamo a cercare").
export const faseDelPercorso = (visite, proposte, immobili, adesso) => {
  const valide = visite.filter((v) => !v.annullata);
  const attive = proposte.filter((p) => !STATI_PROPOSTA_CHIUSI.includes(p.stato));
  const accettate = attive.filter((p) => p.stato === 'accettata');
  if (accettate.some((p) => MUTUO_CONCLUSO.includes(p.mutuo_stato) || (p.rogito_data_prevista && !rogitoSoloPrevisto(p)))) return 5;
  const situazioni = new Map(immobili.map((i) => [i.id, situazioneImmobile(i, valide, proposte, adesso)]));
  // la stessa regola dell'etichetta: lo stato "rogito_in_corso" rimasto con una sola proposta rifiutata (l'accettata
  // eliminata dallo staff) non è più "Offerta accettata", e la fase non resta "Mutuo" per sempre
  if (accettate.length || [...situazioni.values()].some((s) => s.chiave === 'accettata')) return 4;
  if (attive.length || [...situazioni.values()].some((s) => s.chiave === 'trattativa' || inPreparazione(s))) return 3;
  const fuori = (id) => SITUAZIONI_FUORI.includes(situazioni.get(id)?.chiave);
  if (valide.some((v) => visitaInSospeso(v, adesso) && !fuori(v.immobile_id))) return 2;
  return 1;
};

// Quando un passo della banca risulta già fatto, e quindi non è più un appuntamento del cliente: la perizia con il
// mutuo deliberato, erogato o rifiutato; la delibera con il mutuo erogato o rifiutato. Stessa regola dei passi della
// banca qui sotto (PASSI_MUTUO) e del server (passo_banca_fatto in server.py): prima il filtro c'era solo per il mutuo
// rifiutato e la perizia già fatta restava il prossimo appuntamento («20 SET · Perizia della banca»).
const STATI_PASSO_BANCA_FATTO = {
  mutuo_data_perizia: ['delibera', 'erogato', 'rifiutato'],
  mutuo_data_delibera: ['erogato', 'rifiutato'],
};
export const passoBancaFatto = (p, campo) => (STATI_PASSO_BANCA_FATTO[campo] || []).includes(p?.mutuo_stato);

// Le date di una proposta che sono appuntamenti del cliente: proposta, perizia e delibera della banca (se non sono
// già fatte), compromesso, rogito. Le stesse voci del calendario del cliente e del .ics (CAMPI_PROPOSTA in
// backend/agenda.py) e del server (prossimo_appuntamento): prima delibera e compromesso stavano nel calendario e nel
// .ics ma non in home, che nella stessa card diceva «Nessun altro appuntamento fissato».
const campiAppuntamento = (p) => [['proposta', 'data_appuntamento_proposta'],
  ...(passoBancaFatto(p, 'mutuo_data_perizia') ? [] : [['perizia', 'mutuo_data_perizia']]),
  ...(passoBancaFatto(p, 'mutuo_data_delibera') ? [] : [['delibera', 'mutuo_data_delibera']]),
  ['compromesso', 'rogito_data_compromesso'], ['rogito', 'rogito_data_prevista']];

// Un appuntamento che vale tutto il giorno, senza ora né conto alla rovescia: i passi della banca segnati col solo
// giorno (perizia, delibera) e il rogito solo previsto (mutuo ancora in banca o rifiutato: la data non è un
// appuntamento fissato, come dicono il calendario e il .ics con «Rogito previsto»).
export const PASSI_BANCA_APPUNTAMENTO = ['perizia', 'delibera'];
export const appuntamentoInGiornata = (a) => !!a.previsto || (PASSI_BANCA_APPUNTAMENTO.includes(a.tipo) && senzaOrario(a.data));

// Appuntamenti (visite, proposte, perizie, rogiti) da domani in poi; quelli di oggi vanno nella card "Oggi"
export const prossimoAppuntamento = (visite, proposte, immobili, adesso) => {
  const domani = domaniDi(adesso);
  const candidati = [];
  visite.forEach((v) => {
    const data = leggiData(v.data_visita);
    if (data && data >= domani && !v.esito_visita) candidati.push({ tipo: 'visita', data, id: v.id, immobile_id: v.immobile_id, visita: v });
  });
  proposte.forEach((p) => {
    if (STATI_PROPOSTA_CHIUSI.includes(p.stato)) return;
    campiAppuntamento(p).forEach(([tipo, campo]) => {
      const data = leggiData(p[campo]);
      // rogito "previsto": con il mutuo ancora in banca (o rifiutato) la data non è un appuntamento fissato (come il server)
      if (data && data >= domani) candidati.push({ tipo, data, id: p.id, immobile_id: p.immobile_id, proposta: p, previsto: tipo === 'rogito' && rogitoSoloPrevisto(p) });
    });
  });
  if (!candidati.length) return null;
  const primo = candidati.reduce((a, b) => (b.data < a.data ? b : a));
  return { ...primo, immobile: immobili.find((i) => i.id === primo.immobile_id) || null };
};

// Appuntamenti di oggi non ancora passati, in ordine di orario
export const appuntamentiDiOggi = (visite, proposte, immobili, adesso) => {
  const oggi = [];
  const perImmobile = (id) => immobili.find((i) => i.id === id) || null;
  visite.forEach((v) => {
    const data = leggiData(v.data_visita);
    if (data && stessoGiorno(data, adesso) && data > adesso && !v.esito_visita) {
      oggi.push({ tipo: 'visita', data, id: v.id, visita: v, immobile: perImmobile(v.immobile_id) });
    }
  });
  proposte.forEach((p) => {
    if (STATI_PROPOSTA_CHIUSI.includes(p.stato)) return;
    campiAppuntamento(p).forEach(([tipo, campo]) => {
      const data = leggiData(p[campo]);
      if (!data || !stessoGiorno(data, adesso)) return;
      // il rogito "previsto" anche qui (come nel prossimo appuntamento): non è un appuntamento con l'ora
      const voce = { tipo, data, id: `${p.id}-${tipo}`, proposta: p, immobile: perImmobile(p.immobile_id), previsto: tipo === 'rogito' && rogitoSoloPrevisto(p) };
      // la perizia e la delibera segnate solo col giorno, e il rogito solo previsto, restano nella card Oggi per tutto
      // il giorno (prima il rogito previsto diceva «18:00 · TRA 8 ORE · Rogito dal notaio»)
      if (data > adesso || appuntamentoInGiornata(voce)) oggi.push(voce);
    });
  });
  return oggi.sort((a, b) => a.data - b.data);
};

// Una visita già passata che aspetta "Com'è andata?": ora arrivata, senza esito, negli ultimi 30 giorni, non annullata,
// e senza una richiesta di spostamento aperta (il cliente ha chiesto un altro orario: la visita non c'è stata, si
// aspetta il nuovo orario). Una regola sola per la card della home, la riga della card, il calendario e la scheda.
export const chiedeEsito = (visita, adesso) => {
  const data = leggiData(visita.data_visita);
  return !!data && data <= adesso && visitaInSospeso(visita, adesso) && !visita.annullata && !visita.spostamento_richiesto;
};

// La visita è passata ma lo spostamento chiesto dal cliente è ancora aperto: "In attesa del nuovo orario"
export const attendeNuovoOrario = (visita, adesso) => {
  const data = leggiData(visita.data_visita);
  return !!data && data <= adesso && !visita.esito_visita && !visita.annullata && !!visita.spostamento_richiesto;
};

// Visite già avvenute che aspettano "Com'è andata?" (la più recente prima); le case scartate non lo chiedono
export const esitiInSospeso = (visite, immobili, adesso) => {
  const scartati = new Set(immobili.filter((i) => i.stato_attivita === 'scartato').map((i) => i.id));
  return visite
    .filter((v) => chiedeEsito(v, adesso) && !scartati.has(v.immobile_id))
    .sort((a, b) => leggiData(b.data_visita) - leggiData(a.data_visita));
};

// ---------------------------------------------------------------- testi dell'intestazione

// Fino alle 5 di notte è ancora sera: stessa regola del login (ScenaAccesso la prende da qui)
export const momentoDelGiorno = (h = new Date().getHours()) => (
  h < 5 ? 'sera' : h < 12 ? 'mattina' : h < 18 ? 'pomeriggio' : 'sera'
);
export const SALUTI = { mattina: 'Buongiorno', pomeriggio: 'Buon pomeriggio', sera: 'Buonasera' };

// Il saluto nella lingua del cliente (le stesse parole del login: accesso.saluto)
export const saluto = (adesso, lingua = PRINCIPALE) => contesto(lingua).t(`accesso.saluto.${momentoDelGiorno(adesso.getHours())}`);

// "Olena, ci siamo quasi." — il titolo della fase con il nome davanti; senza nome comincia in maiuscolo
export const titoloFase = (nome, fase, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  const titolo = t(`home.titoli.${chiaveFrasiFase(fase)}`);
  return nome ? t('home.titolo_fase', { nome, titolo }) : maiuscola(titolo);
};

// "via Manzoni 14" → "via Manzoni"; senza indirizzo la città
export const luogoBreve = (immobile) => {
  const via = (immobile?.indirizzo || '').split(',')[0].trim().replace(/\s+\d+[a-zA-Z]?(\/\w+)?$/, '');
  if (via) return /^(via|viale|piazza|corso|largo|vicolo|strada|piazzale)\b/i.test(via) ? via.charAt(0).toLowerCase() + via.slice(1) : via;
  return immobile?.citta || '';
};

export const conArticoloLuogo = (immobile) => {
  const luogo = luogoBreve(immobile);
  if (!luogo) return '';
  return immobile?.indirizzo ? `in ${luogo}` : `a ${luogo}`;
};

const CATEGORIE_DA_FARE = { reddito: 'per_mutuo', finanziario: 'per_mutuo', identità: 'per_proposta' };
// Il luogo della visita nella frase: in italiano con la preposizione ("in via Manzoni", "a Monza"), nelle altre
// lingue il luogo nudo (i casi cambiano la parola: la frase lo tiene fra parentesi)
const luogoDelRiepilogo = (immobile, lingua) => (lingua === PRINCIPALE ? conArticoloLuogo(immobile) : luogoBreve(immobile));

// Frase sotto il titolo: "Oggi la visita in via Manzoni; una cosa da fare per il mutuo."
export const riepilogo = ({ oggi, esiti, proposte, daFare, immobili, prossimo, adesso, lingua = PRINCIPALE }) => {
  const { t, tp } = contesto(lingua);
  // Appena arrivati: prima di tutto il primo annuncio (i documenti stanno nella card Da fare)
  if (!immobili.length && !oggi.length && !esiti.length && !prossimo) return t('home.riepilogo.primo_annuncio');
  const parti = [];
  const visiteOggi = oggi.filter((a) => a.tipo === 'visita');
  if (visiteOggi.length === 1) {
    const dove = luogoDelRiepilogo(visiteOggi[0].immobile, lingua);
    const alle = { ora: ora(visiteOggi[0].data) };
    parti.push(dove ? t('home.riepilogo.visita_oggi', { dove, ...alle }) : t('home.riepilogo.visita_oggi_senza_luogo', alle));
  } else if (visiteOggi.length > 1) {
    parti.push(tp('home.riepilogo.plurali.visite_oggi', visiteOggi.length));
  }
  // un rogito solo previsto non è «oggi il rogito»: la data si conferma dopo la delibera
  if (oggi.some((a) => a.tipo === 'rogito' && !a.previsto)) parti.push(t('home.riepilogo.rogito_oggi'));
  else if (oggi.some((a) => a.tipo === 'proposta')) parti.push(t('home.riepilogo.proposta_oggi'));
  if (oggi.some((a) => a.tipo === 'compromesso')) parti.push(t('home.riepilogo.compromesso_oggi'));
  if (oggi.some((a) => a.tipo === 'perizia')) parti.push(t('home.riepilogo.perizia_oggi'));
  if (oggi.some((a) => a.tipo === 'delibera')) parti.push(t('home.riepilogo.delibera_oggi'));
  if (esiti.length === 1) {
    const dove = luogoDelRiepilogo(immobili.find((i) => i.id === esiti[0].immobile_id), lingua);
    parti.push(dove ? t('home.riepilogo.esito_uno', { dove }) : t('home.riepilogo.esito_uno_senza_luogo'));
  } else if (esiti.length > 1) {
    parti.push(tp('home.riepilogo.plurali.esiti', esiti.length));
  }
  const inCorso = proposte.filter((p) => !STATI_PROPOSTA_CHIUSI.includes(p.stato) && p.stato !== 'accettata').length;
  if (inCorso > 0) parti.push(tp('home.riepilogo.plurali.proposte', inCorso));
  if (daFare.length === 1) {
    const perCosa = CATEGORIE_DA_FARE[daFare[0].categoria];
    parti.push(perCosa ? t('home.riepilogo.da_fare_per', { per: t(`home.riepilogo.${perCosa}`) })
      : tp('home.riepilogo.plurali.da_fare', 1));
  } else if (daFare.length > 1) {
    parti.push(tp('home.riepilogo.plurali.da_fare', daFare.length));
  }
  if (!parti.length) {
    if (prossimo) {
      const chiave = ['visita', 'perizia', 'delibera', 'compromesso'].includes(prossimo.tipo) ? prossimo.tipo
        : prossimo.tipo === 'rogito' ? (prossimo.previsto ? 'rogito_previsto' : 'rogito') : 'proposta';
      // il rogito solo previsto non ha un'ora (come nella card del percorso e nel calendario)
      const quando = `${quandoGiorno(prossimo.data, adesso, lingua)}${prossimo.previsto ? '' : alleOra(prossimo.data, lingua)}`;
      parti.push(t('home.riepilogo.prossimo', { cosa: t(`home.riepilogo.cose.${chiave}`), quando }));
    } else {
      parti.push(t('home.riepilogo.lavoriamo'));
    }
  }
  return `${maiuscola(parti.join('; '))}.`;
};

// ---------------------------------------------------------------- la vostra ricerca

// Numero da testo scritto a mano: "240000", "240.000", "1.250.000,00", "92,5", "92 mq"
const numero = (valore) => {
  if (typeof valore === 'number') return valore > 0 ? valore : null;
  let testo = String(valore ?? '').replace(/[^\d.,]/g, '');
  if (/^\d+([.,]\d{1,2})?$/.test(testo)) testo = testo.replace(',', '.');
  else testo = testo.replace(/[.,](?=\d{3}(\D|$))/g, '').replace(',', '.');
  const n = parseFloat(testo);
  return Number.isFinite(n) && n > 0 ? n : null;
};

// "4.185": sempre con il punto delle migliaia (toLocaleString in italiano non lo mette sotto le 10.000)
export const migliaia = (n) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

// "240.000 €" in italiano, "240 000 €" in russo e ucraino (Intl, valutaIn di i18n.js).
// currencyDisplay: 'narrowSymbol' perché in ucraino il formato lungo scrive "240 000 EUR" invece del simbolo.
export const formatEuro = (valore, lingua = PRINCIPALE) => {
  const n = numero(valore);
  if (!n) return null;
  return lingua === PRINCIPALE ? `${migliaia(n)} €` : contesto(lingua).valuta(Math.round(n), { currencyDisplay: 'narrowSymbol' });
};

// "Vimercate, Arcore · zona centro": i comuni della ricerca e le zone o vie preferite
export const zoneRicerca = (cliente) => {
  const parti = [(cliente?.citta_ricerca || []).filter(Boolean).join(', '), cliente?.zone_preferite?.trim()].filter(Boolean);
  return parti.length ? parti.join(' · ') : null;
};

// "180.000 – 240.000 €" con il budget minimo, "240.000 €" (budget massimo) o "da 180.000 €" (solo il minimo).
// Un minimo più alto del massimo (salvato prima dei controlli) si mostra lo stesso, con l'avviso.
export const budgetRicerca = (cliente, lingua = PRINCIPALE) => {
  const { t, numero: num } = contesto(lingua);
  const massimo = numero(cliente?.budget_max);
  const minimo = numero(cliente?.budget_min);
  const euro = (n) => formatEuro(n, lingua);
  const soloCifra = (n) => (lingua === PRINCIPALE ? migliaia(n) : num(Math.round(n)));
  if (!massimo && !minimo) return { budget: null, etichetta: t('home.ricerca.budget_massimo') };
  if (!massimo) return { budget: t('home.ricerca.da_minimo', { importo: euro(minimo) }), etichetta: t('home.ricerca.budget_minimo') };
  if (!minimo) return { budget: euro(massimo), etichetta: t('home.ricerca.budget_massimo') };
  if (minimo === massimo) return { budget: euro(massimo), etichetta: t('home.ricerca.budget') };
  return { budget: `${soloCifra(minimo)} – ${euro(massimo)}`, etichetta: t(minimo < massimo ? 'home.ricerca.budget' : 'home.ricerca.budget_errato') };
};

// "No piano terra, giardino, box o posto auto": piano e cose da avere
const PIANI_TESTO = { no_piano_terra: 'no_piano_terra', ultimo: 'ultimo', ascensore: 'ascensore' };
export const daAvere = (cliente, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  const piani = cliente?.piano_ricerca?.length ? cliente.piano_ricerca : cliente?.piano_preferito === 'alto' ? ['no_piano_terra'] : [];
  const parti = [...piani.map((p) => (PIANI_TESTO[p] ? t(`home.ricerca.piani.${PIANI_TESTO[p]}`) : null)).filter(Boolean),
    ...[['con_giardino', 'avere_giardino'], ['con_terrazzo', 'avere_terrazzo'], ['con_box', 'avere_box']]
      .filter(([k]) => cliente?.[k]).map(([, chiave]) => t(`home.ricerca.${chiave}`))];
  return parti.length ? maiuscola(parti.join(', ')) : null;
};

const TIPI = {
  appartamento: 'appartamento', villa: 'villa', villa_a_schiera: 'villa_a_schiera', casa_indipendente: 'casa_indipendente',
  attico: 'attico', loft: 'loft', rustico: 'rustico', terreno: 'terreno', bilocale: 'bilocale', trilocale: 'trilocale', quadrilocale: 'quadrilocale',
};
const LOCALI = { 1: 'monolocale', 2: 'bilocale', 3: 'trilocale', 4: 'quadrilocale' };
const TAGLI = { monolocale: 1, mono: 1, bilo: 2, bilocale: 2, trilo: 3, trilocale: 3, quadri: 4, quadrilocale: 4, cinque_locali: 5, oltre: 5 };
const nomeTipo = (tipo, t) => (TIPI[tipo] ? t(`home.ricerca.tipi.${TIPI[tipo]}`) : String(tipo).replace(/_/g, ' ').toLowerCase());

// "Appartamento, villa, trilocale, 2 bagni, almeno 70 m²" dai campi della ricerca (tipi, locali, bagni e metri
// minimi): una frase sola, tutta minuscola tranne la prima lettera (prima "Appartamento, Villa, trilocale")
export const cosaCercate = (cliente, lingua = PRINCIPALE) => {
  const { t, tp, numero: num } = contesto(lingua);
  const tipi = (cliente?.tipo_immobile_ricerca || []).map((x) => nomeTipo(x, t));
  if (!tipi.length && cliente?.tipo_immobile) tipi.push(nomeTipo(cliente.tipo_immobile, t));
  let locali = Math.floor(numero(cliente?.locali_min) || 0);
  if (!locali && cliente?.taglio_immobile?.length) {
    const tagli = cliente.taglio_immobile.map((x) => TAGLI[x]).filter(Boolean);
    if (tagli.length) locali = Math.min(...tagli);
  }
  const parti = [...tipi.slice(0, 3)];
  if (locali) {
    const taglio = LOCALI[locali] ? t(`home.ricerca.tipi.${LOCALI[locali]}`) : tp('home.ricerca.plurali.locali_min', locali);
    if (!parti.includes(taglio)) parti.push(taglio);
  }
  // locali e bagni sono numeri interi ("2,5" salvato prima dei controlli vale 2)
  const bagni = Math.floor(numero(cliente?.bagni_min ?? cliente?.bagni) || 0);
  if (bagni) parti.push(tp('home.ricerca.plurali.bagni', bagni));
  // "1" salvato prima dei controlli (da "0,5") non è una metratura: niente "almeno 1 m²"
  const metri = numero(cliente?.metratura_min);
  if (metri >= 10) parti.push(t('home.ricerca.metri_min', { metri: lingua === PRINCIPALE ? migliaia(metri) : num(Math.round(metri)) }));
  return parti.length ? maiuscola(parti.join(', ')) : null;
};

// "dall'8 agosto" e "con noi da 34 giorni"
export const conNoiDal = (dataRegistrazione, adesso, lingua = PRINCIPALE) => {
  const { t, tp } = contesto(lingua);
  const d = leggiDataUtcRoma(dataRegistrazione); // il giorno di Roma, come "adesso"
  if (!d) return null;
  const giorni = Math.max(0, giorniTra(d, adesso));
  const anno = d.getFullYear() !== adesso.getFullYear() ? ` ${d.getFullYear()}` : '';
  // in italiano l'articolo si elide davanti a 1, 8 e 11 ("dall'8 agosto"); nelle altre lingue la data sta nella chiave
  const dal = lingua === PRINCIPALE
    ? `${[1, 8, 11].includes(d.getDate()) ? "dall'" : 'dal '}${giornoEMese(d)}${anno}`
    : t('home.ricerca.dal', { data: scrittaConAnno(lingua, d, adesso, { day: 'numeric', month: 'long' }) });
  return { dal, da: giorni === 0 ? t('home.ricerca.con_noi_oggi') : tp('home.ricerca.plurali.con_noi', giorni) };
};

// ---------------------------------------------------------------- immobili

export const dominio = (link) => {
  try {
    return new URL(/^https?:\/\//.test(link) ? link : `https://${link}`).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

// Gli stati sono due: "da_compilare" (c'è solo il link, l'estrattore non l'ha ancora letto) e "completato" (tutto
// il resto). Un annuncio letto dall'estrattore è già completo: niente "dati in verifica" per il cliente.
export const daCompilare = (immobile) => immobile.stato_elaborazione === 'da_compilare';

export const titoloImmobile = (immobile, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  if (daCompilare(immobile)) return t('immobili.titolo_annuncio', { sito: dominio(immobile.link) || t('home.novita_testi.sito_sconosciuto') });
  return immobile.titolo || t('immobili.titolo_generico');
};

// Titolo senza la città in coda ("Trilocale via Manzoni 14, Cernusco s/N" → "Trilocale via Manzoni 14")
export const titoloBreve = (immobile, lingua = PRINCIPALE) => titoloImmobile(immobile, lingua).split(',')[0].trim();

export const metriQuadri = (superficie) => {
  const n = numero(String(superficie ?? '').split(/[^\d.,]/)[0] || superficie);
  return n ? `${Math.round(n)} m²` : null;
};

// Una sola etichetta per immobile, con la scala colori della bozza:
// nuovo (blu) · visita (ambra) · proposta (oro) · chiuso (verde) · scartato (grigio)
// Motivi di scarto (chiavi del catalogo dello staff, MotivoScarto.jsx) come li legge il cliente
export const MOTIVI_SCARTO_BREVI = {
  prezzo_alto: 'prezzo', zona_non_adatta: 'zona', condizioni_immobile: 'stato', metratura_inadeguata: 'metratura',
  piano_non_adatto: 'piano', mancanza_requisiti: 'requisiti', agenzia_non_collaborativa: 'agenzia',
  gia_venduto: 'già venduto', cliente_non_interessato: 'non interessati',
};
const ETICHETTE_MOTIVI_VISITA = Object.fromEntries(MOTIVI.map(([k]) => [k, k]));
// Il motivo della visita andata male, come lo legge il cliente **dentro una riga** ("prezzo", "zona"): le due
// parole corte (`immobili.motivi_brevi`), non la frase intera dell'elenco in cui si sceglie.
export const motivoVisitaBreve = (chiave, lingua = PRINCIPALE) => (ETICHETTE_MOTIVI_VISITA[chiave]
  ? contesto(lingua).t(`immobili.motivi_brevi.${chiave}`).toLowerCase() : null);
// Il motivo di scarto del catalogo dello staff come lo legge il cliente ("già venduto", "zona")
export const motivoScartoBreve = (chiave, lingua = PRINCIPALE) => (MOTIVI_SCARTO_BREVI[chiave]
  ? contesto(lingua).t(`immobili.motivi_scarto.${chiave}`) : null);

// Il giorno con l'articolo per l'italiano ("il 26 febbraio", "l'8 marzo"), la sola data nelle altre lingue
const giornoConArticolo = (d, adesso, lingua) => (lingua === PRINCIPALE
  ? conAnno(ilGiorno(giornoEMese(d)), d, adesso) : scrittaConAnno(lingua, d, adesso, { day: 'numeric', month: 'long' }));

const etichettaDiBase = (immobile, visite, proposte, adesso, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  // la regola di situazioneImmobile (la stessa della fase e del server), qui con le parole
  const s = situazioneImmobile(immobile, visite, proposte, adesso);
  switch (s.chiave) {
    case 'scartato': {
      const motivo = motivoScartoBreve(immobile.motivo_scarto, lingua);
      const quale = immobile.scartato_dal_cliente ? 'non_vi_interessa' : 'scartato';
      return { classe: 'scartato', testo: motivo ? t(`immobili.etichette.${quale}_motivo`, { motivo }) : t(`immobili.etichette.${quale}`) };
    }
    case 'accettata': {
      const accettata = s.proposta;
      // mutuo rifiutato dalla banca: niente "Rogito domenica 25 ottobre" (il rogito è solo previsto, fase Mutuo)
      if (accettata?.mutuo_stato === 'rifiutato') return { classe: 'chiuso', testo: t('immobili.etichette.mutuo_non_concesso') };
      const rogito = accettata && leggiData(accettata.rogito_data_prevista);
      if (!rogito) return { classe: 'chiuso', testo: t('immobili.etichette.accettata') };
      // con il mutuo ancora in banca la data è solo prevista: il rogito si fissa dopo la delibera (fase Mutuo)
      // con l'anno se il rogito è di un altro anno (conAnno): prima "Rogito previsto il 26 febbraio" anche per il 2027
      if (mutuoInCorso(accettata)) {
        // una data prevista già passata non si annuncia al futuro: si torna a "Offerta accettata", come quando la
        // data non c'è (prima "Rogito previsto il 10 settembre" per giorni, con la card che diceva "Nessun altro
        // appuntamento fissato")
        if (giorniTra(adesso, rogito) < 0) return { classe: 'chiuso', testo: t('immobili.etichette.accettata') };
        return { classe: 'chiuso', testo: t('immobili.etichette.rogito_previsto', { quando: giornoConArticolo(rogito, adesso, lingua) }) };
      }
      return { classe: 'chiuso', testo: t('immobili.etichette.rogito', { quando: quandoGiorno(rogito, adesso, lingua) }) };
    }
    // dopo il rifiuto del venditore lo stato dello staff resta "proposta in corso": conta la proposta
    case 'trattativa': return { classe: 'proposta', testo: t('immobili.etichette.trattativa') };
    case 'non_accettata': return { classe: 'scartato', testo: t('immobili.etichette.non_accettata') };
    case 'visita':
      // la stessa forma della riga della visita sulla card ("Visita giovedì 17 · 16:00", "Visita oggi · 16:00"):
      // prima l'etichetta diceva "Visita giovedì · 16:00" e la riga sotto "Visita giovedì 17 · 16:00 con Ernesto"
      if (s.futura) return { classe: 'visita', testo: t('immobili.etichette.visita', { quando: quandoVisita(s.futura.data_visita, adesso, lingua) }) };
      // l'ora è passata ma il cliente aveva chiesto di spostarla: la visita non c'è stata, aspetta il nuovo orario
      if (s.nuovoOrario) return { classe: 'visita', testo: t('immobili.etichette.nuovo_orario') };
      return { classe: 'visita', testo: t('immobili.etichette.com_e_andata') };
    case 'richiesta': return { classe: 'nuovo', testo: t('immobili.etichette.visita_richiesta') };
    case 'non_fa_per_voi': {
      const motivo = motivoVisitaBreve(s.ultimo.motivi?.[0], lingua);
      return { classe: 'scartato', testo: motivo ? t('immobili.etichette.non_fa_per_voi_motivo', { motivo }) : t('immobili.etichette.non_fa_per_voi') };
    }
    case 'preparazione': return { classe: 'proposta', testo: t('immobili.etichette.preparazione') };
    // piaciuta più di 30 giorni fa, senza proposta: non è più "in preparazione" (né in trattativa) per la fase
    // con l'anno se la visita è di un altro anno (conAnno): prima "visitata il 10 novembre" anche per il 2025
    case 'piaciuta': return { classe: 'nuovo', testo: t('immobili.etichette.piaciuta', { quando: giornoConArticolo(leggiData(s.ultimo.data_visita), adesso, lingua) }) };
    default: break;
  }
  if (immobile.stato_attivita === 'visita_fissata') return { classe: 'visita', testo: t('immobili.etichette.visita_fissata') };
  if (immobile.da_decidere) {
    return { classe: 'nuovo', testo: immobile.inserito_da_nome ? t('immobili.etichette.proposto_da', { nome: immobile.inserito_da_nome }) : t('immobili.etichette.proposto_da_noi') };
  }
  if (daCompilare(immobile)) return { classe: 'nuovo', testo: t('immobili.etichette.in_compilazione') };
  if (['da_contattare', 'non_risponde', 'richiamare'].includes(immobile.stato_attivita)) return { classe: 'nuovo', testo: t('immobili.etichette.sentiamo_agenzia') };
  if (immobile.stato_attivita === 'attesa_disponibilita') return { classe: 'visita', testo: t('immobili.etichette.quando_visitarla') };
  if (immobile.stato_attivita === 'da_visionare') return { classe: 'nuovo', testo: t('immobili.etichette.si_puo_visitare') };
  return { classe: 'nuovo', testo: t('immobili.etichette.nuovo') };
};

// Annuncio tolto dal portale (annuncio_non_online dal server) e casa ancora all'inizio: finché l'agenzia non conferma
// che è ancora in vendita non è "da decidere" né "si può visitare". Vale in home come in /immobili (prima la home
// diceva "Proposto da Karolina", come se fosse da decidere).
export const etichettaImmobile = (immobile, visite, proposte, adesso, lingua = PRINCIPALE) => {
  const etichetta = etichettaDiBase(immobile, visite, proposte, adesso, lingua);
  // «Venduto» viene prima di tutto: è l'unico stato da cui non si torna, e per chi guarda la sua lista è
  // l'informazione che conta più di ogni altra. Al cliente non arriva nessuna notifica — una casa che non si
  // può più comprare si dice a voce — ma nella lista la deve vedere (backend/stato_annuncio.py).
  //
  // La classe è `scartato`, cioè «fuori ricerca», e non è una scelta di comodo: quella casa non si può più
  // comprare. Così finisce nel filtro «Scartati» con l'etichetta «Venduto» (dove si vede), e `fuoriRicerca`
  // spegne «prenotate un'altra visita» e «non ci interessa più», che su una casa venduta non hanno senso. Il
  // prezzo di questa scelta: non compare fra le tre case recenti della home, che mostra quelle ancora in gioco.
  const chiuso = immobile.venduto?.stato;
  if (chiuso === 'venduto' || chiuso === 'affittato') {
    return { classe: 'scartato', testo: contesto(lingua).t(`immobili.etichette.${chiuso}`) };
  }
  return immobile.annuncio_non_online && etichetta.classe === 'nuovo'
    ? { classe: 'nuovo', testo: contesto(lingua).t('immobili.etichette.non_online') } : etichetta;
};

// I 3 più recenti tra quelli ancora in gioco (niente scartati né "non fa per voi"), mostrati nell'ordine di
// inserimento come in /immobili: il più vecchio dei tre in alto, il più recente in fondo
export const immobiliRecenti = (immobili, visite, proposte, adesso, quanti = 3, lingua = PRINCIPALE) => immobili
  .map((immobile, n) => ({ immobile, n, etichetta: etichettaImmobile(immobile, visite, proposte, adesso, lingua) }))
  .filter(({ etichetta }) => etichetta.classe !== 'scartato')
  .sort((a, b) => ((leggiDataUtc(a.immobile.data_aggiunta) ?? -Infinity) - (leggiDataUtc(b.immobile.data_aggiunta) ?? -Infinity)) || a.n - b.n)
  .slice(-quanti)
  .map(({ immobile, etichetta }) => ({ immobile, etichetta }));

// ---------------------------------------------------------------- articoli ("il bilocale in via Marconi 5")

// Il tipo di immobile all'inizio del titolo decide articolo e genere
const ARTICOLI_IMMOBILE = [
  [/^(appartamento|attico|open space|immobile|annuncio|ufficio|edificio)\b/i, "l'", false],
  [/^(villa|villetta|casa|mansarda|porzione|cascina|baita)\b/i, 'la', true],
  [/^(monolocale|bilocale|trilocale|quadrilocale|pentalocale|loft|rustico|box|terreno|capannone|negozio|garage)\b/i, 'il', false],
];
const NOMI_STRADE = 'via|viale|piazza|piazzale|corso|largo|vicolo|strada|località|contrada';
const PREPOSIZIONI = {
  di: { "l'": "dell'", il: 'del', la: 'della', lo: 'dello' },
  a: { "l'": "all'", il: 'al', la: 'alla', lo: 'allo' },
};
const minuscola = (t) => (t ? t.charAt(0).toLowerCase() + t.slice(1) : t);

// "Bilocale via Marconi 5" → { articolo: 'il', testo: 'bilocale in via Marconi 5', f: false };
// "Via Roma 22" → "l'immobile in via Roma 22"; un titolo che non comincia con un tipo resta com'è, senza articolo
export const immobileConArticolo = (titolo) => {
  const t = String(titolo || '').trim();
  if (new RegExp(`^(${NOMI_STRADE})\\b`, 'i').test(t)) return { articolo: "l'", testo: `immobile in ${minuscola(t)}`, f: false };
  const trovato = ARTICOLI_IMMOBILE.find(([re]) => re.test(t));
  if (!trovato) return { articolo: '', testo: t, f: false };
  const testo = minuscola(t).replace(new RegExp(`\\s(in\\s+)?(${NOMI_STRADE})\\s`, 'i'), (_, gia, strada) => ` in ${strada.toLowerCase()} `);
  return { articolo: trovato[1], testo, f: trovato[2] };
};

// L'immobile con l'articolo, anche dopo "di", "a", "per": "del bilocale in via Marconi 5", "all'attico…"
export const conArticolo = ({ articolo, testo }, preposizione = '') => {
  if (!articolo) return preposizione ? `${preposizione} ${testo}` : testo;
  const art = preposizione ? (PREPOSIZIONI[preposizione]?.[articolo] || `${preposizione} ${articolo}`) : articolo;
  return art.endsWith("'") ? `${art}${testo}` : `${art} ${testo}`;
};

// ---------------------------------------------------------------- novità (cronologia)

// im = l'immobile con l'articolo (immobileConArticolo)
const STATI_NOVITA = {
  da_contattare: ['k', (im) => `Stiamo sentendo l'agenzia ${conArticolo(im, 'per')}`],
  non_risponde: ['', (im) => `L'agenzia ${conArticolo(im, 'di')} non risponde ancora`],
  richiamare: ['', (im) => `Richiameremo l'agenzia ${conArticolo(im, 'per')}`],
  da_visionare: ['g', (im) => `${maiuscola(conArticolo(im))} si può visitare`],
  visita_fissata: ['k', (im) => `Visita fissata ${conArticolo(im, 'per')}`],
  scartato: ['', (im) => `${maiuscola(conArticolo(im))} è stat${im.f ? 'a' : 'o'} scartat${im.f ? 'a' : 'o'}`],
  proposta_in_corso: ['k', (im) => `Proposta in corso ${conArticolo(im, 'per')}`],
  rogito_in_corso: ['g', (im) => `Offerta accettata ${conArticolo(im, 'per')}`],
};
// La stessa voce nelle altre lingue: le chiavi di home.novita_testi ("{immobile}: scartato"), perché in russo e in
// ucraino la frase non si compone a pezzi (il caso cambia la parola, il genere cambia il verbo)
const CHIAVI_STATI_NOVITA = {
  da_contattare: 'da_contattare', non_risponde: 'non_risponde', richiamare: 'richiamare', da_visionare: 'da_visionare',
  visita_fissata: 'visita_fissata_stato', scartato: 'scartato', proposta_in_corso: 'proposta_in_corso', rogito_in_corso: 'offerta_accettata',
};

// Una frase di Novità: in italiano si compone con l'articolo e l'accordo ("Il bilocale in via Marconi 5 è stato
// scartato"); nelle altre lingue è una frase intera con il titolo dell'immobile così com'è.
const frase = (lingua, italiana, chiave, valori) => (lingua === PRINCIPALE ? italiana()
  : contesto(lingua).t(`home.novita_testi.${chiave}`, valori));

// Un evento della cronologia in parole del cliente; null = da non mostrare
export const testoNovita = (evento, immobili, adesso, lingua = PRINCIPALE) => {
  const immobile = immobili.find((i) => i.id === evento.immobile_id);
  const t = immobile ? titoloBreve(immobile, lingua) : contesto(lingua).t('home.novita_testi.un_immobile');
  const im = immobile ? immobileConArticolo(t) : { articolo: '', testo: t, f: false };
  const con = { immobile: t };
  const meta = evento.metadata || {};
  const chi = evento.autore_nome; // chi dello staff l'ha fatto ("Karolina"), dal backend
  switch (evento.tipo_evento) {
    case 'immobile_aggiunto': {
      const sito = (immobile && dominio(immobile.link)) || (evento.descrizione || '').match(/da ([^\s(]+)/)?.[1]
        || contesto(lingua).t('home.novita_testi.sito_sconosciuto');
      if (evento.utente_tipo === 'cliente') return { tipo: 'b', testo: frase(lingua, () => `Avete aggiunto un annuncio da ${sito}`, 'annuncio_vostro', { sito }) };
      return { tipo: 'k',
        testo: chi ? frase(lingua, () => `${chi} ha aggiunto un annuncio da ${sito}`, 'annuncio_chi', { chi, sito })
          : frase(lingua, () => `Abbiamo aggiunto un annuncio da ${sito}`, 'annuncio_nostro', { sito }) };
    }
    case 'dati_compilati': {
      if (!immobile) return null;
      const fatto = meta.verificati ? 'verificato' : 'compilato';
      const chiave = `scheda_${meta.verificati ? 'verificata' : 'compilata'}${chi ? '_chi' : ''}`;
      return { tipo: 'b',
        testo: frase(lingua, () => (chi ? `${chi} ha ${fatto} la scheda ${conArticolo(im, 'di')}` : `Abbiamo ${fatto} la scheda ${conArticolo(im, 'di')}`),
          chiave, { ...con, chi }) };
    }
    case 'dati_estratti': return immobile
      ? { tipo: 'b', testo: frase(lingua, () => `Scheda ${conArticolo(im, 'di')} pronta con i dati dell'annuncio`, 'dati_estratti', con) } : null;
    case 'stato_cambiato': {
      if (evento.stato_nuovo === 'scartato' && evento.utente_tipo === 'cliente') {
        return { tipo: '', testo: frase(lingua, () => `Avete detto che ${conArticolo(im)} non vi interessa`, 'non_vi_interessa', con) };
      }
      const voce = STATI_NOVITA[evento.stato_nuovo];
      return voce ? { tipo: voce[0], testo: frase(lingua, () => voce[1](im), CHIAVI_STATI_NOVITA[evento.stato_nuovo], con) } : null;
    }
    case 'visita_fissata': {
      // "Karolina ha fissato la visita al bilocale in via Marconi 5 per domenica alle 10:30" (prima "…la visita
      // domenica alle 10:30: Bilocale via Marconi 5", senza articolo)
      const d = leggiData(meta.data_visita);
      const quando = d ? ` per ${quandoGiorno(d, adesso)} alle ${ora(d)}` : '';
      const altra = d ? { quando: contesto(lingua).t('home.riepilogo.alle_ore', { quando: quandoGiorno(d, adesso, lingua), ora: ora(d) }) } : {};
      const casa = conArticolo(im, 'a');
      const dalloStaff = chi && evento.utente_tipo !== 'cliente';
      const chiave = `visita_fissata${dalloStaff ? '_chi' : ''}${d ? '' : '_senza'}`;
      return { tipo: 'k',
        testo: frase(lingua, () => (dalloStaff ? `${chi} ha fissato la visita ${casa}${quando}` : `Visita ${casa} fissata${quando}`),
          chiave, { ...con, ...altra, chi }) };
    }
    case 'visita_effettuata':
      return meta.esito === 'positivo'
        ? { tipo: 'g', testo: frase(lingua, () => `Vi è piaciut${im.f ? 'a' : 'o'} ${conArticolo(im)}`, 'piaciuta', con) }
        : { tipo: '', testo: frase(lingua, () => `${maiuscola(conArticolo(im))}: non fa per voi`, 'non_fa_per_voi', con) };
    case 'proposta_creata': return { tipo: 'k', testo: frase(lingua, () => `Proposta inviata ${conArticolo(im, 'per')}`, 'proposta_inviata', con) };
    case 'proposta_aggiornata': return testoProposta(evento, im, lingua, t);
    case 'immobile_eliminato': return { tipo: '', testo: contesto(lingua).t('home.novita_testi.immobile_eliminato') };
    case 'messaggio': return { tipo: 'k',
      testo: frase(lingua, () => `Messaggio ${chi ? `da ${chi}` : 'dal vostro consulente'}: ${evento.descrizione}`,
        chi ? 'messaggio_chi' : 'messaggio', { chi, testo: evento.descrizione }) };
    case 'spostamento_richiesto': return { tipo: 'b', testo: frase(lingua, () => `Avete chiesto di spostare la visita ${conArticolo(im, 'a')}`, 'spostamento_richiesto', con) };
    case 'visita_richiesta': return { tipo: 'b', testo: frase(lingua, () => `Avete chiesto di visitare ${conArticolo(im)}`, 'visita_richiesta', con) };
    case 'immagine_caricata': return null;
    default: return testoNovitaAltro(evento, immobile, im, chi, lingua, t);
  }
};

// Una voce della proposta, dal testo per il cliente che il server mette nella descrizione (metadata.testo_cliente):
// "Il venditore di via Manzoni 14 ha risposto con una controproposta: 375.000 €", "Abbiamo presentato la vostra nuova
// offerta: 368.000 €" (o i testi di PUT /proposte: "Controproposta del venditore: …", "Nuova offerta: …"). Prima si
// riconosceva solo la descrizione che comincia con "Controproposta": controproposta e rilancio dalla scheda dello staff
// erano "Proposta aggiornata per…". La risposta del venditore registrata nei dati di prima con due voci (il cambio di
// stato e "Il venditore… ha accettato/rifiutato"): una riga sola, quella del cambio di stato.
const IMPORTO_FINALE = /(\d{1,3}(?:\.\d{3})+(?:,\d+)? €)\s*$/;
// titolo = il titolo breve dell'immobile, per le lingue in cui la frase non si compone a pezzi
export const testoProposta = (evento, im, lingua = PRINCIPALE, titolo = '') => {
  const con = { immobile: titolo };
  if (evento.stato_nuovo === 'accettata') return { tipo: 'g', testo: frase(lingua, () => `Offerta accettata ${conArticolo(im, 'per')}`, 'offerta_accettata', con) };
  if (STATI_PROPOSTA_CHIUSI.includes(evento.stato_nuovo)) {
    return { tipo: '', testo: frase(lingua, () => `Proposta non accettata ${conArticolo(im, 'per')}`, 'proposta_non_accettata', con) };
  }
  const d = String(evento.descrizione || '');
  if (/^Il venditore\b.* ha (accettato|rifiutato) la vostra proposta/.test(d)) return null;
  const importo = d.match(IMPORTO_FINALE)?.[1];
  const conImporto = importo ? `: ${importo}` : '';
  const suffisso = importo ? '_importo' : '';
  if (/controproposta/i.test(d)) {
    return { tipo: 'k', testo: frase(lingua, () => `Controproposta del venditore ${conArticolo(im, 'per')}${conImporto}`, `controproposta${suffisso}`, { ...con, importo }) };
  }
  if (/nuova offerta/i.test(d)) {
    return { tipo: 'k', testo: frase(lingua, () => `Abbiamo presentato la vostra nuova offerta ${conArticolo(im, 'per')}${conImporto}`, `nuova_offerta${suffisso}`, { ...con, importo }) };
  }
  // i passi della banca, con il testo per il cliente del server ("Mutuo: la banca ha deliberato il mutuo (BPM)",
  // "La banca non ha concesso il mutuo: Maksym vi chiama per vedere le alternative")
  if (/^Mutuo: /.test(d)) return { tipo: 'k', testo: d };
  if (/^La banca non ha concesso il mutuo\b/.test(d)) return { tipo: '', testo: d };
  // le altre modifiche dello staff (note interne, date, caparra: "Proposta aggiornata: modifica"): come nella storia
  // della casa (voceStoria di utils/immobili.js) non si mostrano. Prima «Proposta aggiornata per il trilocale…» anche
  // quando lo staff cambiava solo le sue note
  if (/^Proposta aggiornata\b/.test(d) || !d) return null;
  return { tipo: 'k', testo: frase(lingua, () => `Proposta aggiornata ${conArticolo(im, 'per')}`, 'proposta_aggiornata', con) };
};

// Le altre voci della cronologia che il cliente vede. Il server scrive la descrizione per lo staff ("Maksym ha chiesto
// estratti conto", "Disponibilità per le visite aggiornata") o, per alcune, un testo per il cliente senza articoli
// ("Avete caricato buste paga"): qui diventano frasi al "voi" con gli articoli. Un tipo che non è qui non si mostra
// (prima passava la descrizione grezza: anche gli accessi con il codice o la passkey).
const DOCUMENTO_GENERICO = /^(un|una|uno) |^\d+ document/i; // "un documento", "3 documenti" (non "730 / modello Redditi")
// I nomi dei documenti arrivano dal server in italiano: fuori dall'italiano si scrivono così come sono, senza articolo
const fraseDoc = (nome, lingua = PRINCIPALE) => (lingua !== PRINCIPALE || DOCUMENTO_GENERICO.test(nome) ? nome : fraseDocumento(nome));
// "buste paga, estratti conto e carta d'identità" → "le buste paga, l'estratto conto e la carta d'identità"
const fraseDocumenti = (nomi, lingua = PRINCIPALE) => (lingua !== PRINCIPALE ? String(nomi)
  : elencoParole(String(nomi).split(/, | e /).map((n) => fraseDoc(n.trim())).filter(Boolean)));
const elencoParole = (p) => (p.length <= 1 ? p.join('') : `${p.slice(0, -1).join(', ')} e ${p[p.length - 1]}`);

const testoNovitaAltro = (evento, immobile, im, chi, lingua = PRINCIPALE, titolo = '') => {
  const d = String(evento.descrizione || '').trim();
  const cliente = evento.utente_tipo === 'cliente';
  const doc = (nome) => fraseDoc(nome, lingua);
  const meta = evento.metadata || {};
  let m;
  switch (evento.tipo_evento) {
    // Un comune entrato (o uscito) da solo dalle zone di ricerca, dall'immobile inserito. Il server scrive per il
    // cliente "Saronno è entrato fra le vostre zone di ricerca" (metadata.testo_cliente), in italiano e senza i
    // dati extra: il nome del comune si prende da lì e la frase si rifà nella lingua del cliente. Senza questa
    // voce la riga si vedeva solo nella scheda dello staff.
    case 'comune_imparato': {
      const dalTesto = d.match(/^(.+?) è (entrat|uscit)[oa]\b/);
      const comune = (typeof meta.comune === 'string' && meta.comune.trim()) || (dalTesto && dalTesto[1]) || '';
      if (!comune) return null;
      const uscito = Boolean(meta.rifiutato) || (dalTesto && dalTesto[2] === 'uscit');
      return uscito
        ? { tipo: '', testo: frase(lingua, () => `${comune} è uscito dalle vostre zone di ricerca`, 'comune_uscito', { comune }) }
        : { tipo: 'b', testo: frase(lingua, () => `${comune} è entrato fra le vostre zone di ricerca`, 'comune_entrato', { comune }) };
    }
    case 'disponibilita_aggiornata':
      if (cliente) return { tipo: 'b', testo: frase(lingua, () => 'Avete aggiornato la vostra disponibilità per le visite', 'disponibilita_vostra') };
      return { tipo: 'k',
        testo: frase(lingua, () => `${chi ? `${chi} ha segnato` : 'Abbiamo segnato'} la vostra disponibilità per le visite: controllate che sia giusta`,
          chi ? 'disponibilita_chi' : 'disponibilita_nostra', { chi }) };
    case 'documenti_richiesti':
      if ((m = d.match(/^(.+?) ha chiesto (.+?)(?: entro (?:il |l')(.+))?$/))) {
        const quando = m[3];
        return { tipo: 'k',
          testo: frase(lingua, () => `${chi || m[1]} vi ha chiesto ${fraseDocumenti(m[2])}${quando ? ` entro ${ilGiorno(quando)}` : ''}`,
            quando ? 'documenti_chiesti_entro' : 'documenti_chiesti', { chi: chi || m[1], documenti: fraseDocumenti(m[2], lingua), quando }) };
      }
      // i documenti periodici da rinnovare ("Richiesta aggiornata: le buste paga di settembre")
      if ((m = d.match(/^Richiesta aggiornata: (.+)$/))) {
        return { tipo: 'k', testo: frase(lingua, () => `Documenti da aggiornare: ${m[1]}`, 'documenti_aggiornare', { documenti: m[1] }) };
      }
      return null;
    case 'documento_caricato':
      if ((m = d.match(/^Avete caricato (.+)$/))) return { tipo: 'b', testo: frase(lingua, () => `Avete caricato ${doc(m[1])}`, 'documento_caricato_voi', { documento: doc(m[1]) }) };
      if ((m = d.match(/^(.+?) ha caricato (.+)$/))) {
        return { tipo: 'b', testo: frase(lingua, () => `${chi || m[1]} ha caricato ${doc(m[2])}`, 'documento_caricato_chi', { chi: chi || m[1], documento: doc(m[2]) }) };
      }
      return null; // i moduli firmati: c'è già "Avete firmato…"
    case 'documento_verificato':
      if ((m = d.match(/^(.+) verificat[oa] da (.+)$/))) {
        return { tipo: 'g', testo: frase(lingua, () => `${chi || maiuscola(m[2])} ha verificato ${doc(m[1])}`, 'documento_verificato', { chi: chi || maiuscola(m[2]), documento: doc(m[1]) }) };
      }
      return null;
    case 'documento_rifiutato':
      if ((m = d.match(/^(.+?) da ricaricare: (.+?)(?: \([^)]*\))?$/))) {
        return { tipo: '', testo: frase(lingua, () => `Da ricaricare: ${doc(m[1])} (${m[2]})`, 'documento_rifiutato', { documento: doc(m[1]), motivo: m[2] }) };
      }
      return null;
    case 'documento_tolto':
      if ((m = d.match(/^Avete tolto (.+)$/))) return { tipo: '', testo: frase(lingua, () => `Avete tolto ${doc(m[1])}`, 'documento_tolto', { documento: doc(m[1]) }) };
      return null;
    case 'documenti_inviati':
      if ((m = d.match(/^Documenti consegnati a (.+)$/))) {
        return { tipo: 'g', testo: frase(lingua, () => `Abbiamo consegnato i vostri documenti a ${m[1]}`, 'documenti_inviati', { chi: m[1] }) };
      }
      return null;
    case 'firma_richiesta':
      if ((m = d.match(/^Da firmare: (.+)$/))) {
        const nome = conArticoloDocumento(m[1], lingua);
        return { tipo: 'k',
          testo: frase(lingua, () => `${chi ? `${chi} vi ha mandato` : 'Vi abbiamo mandato'} da firmare ${nome}`,
            chi ? 'firma_richiesta_chi' : 'firma_richiesta', { chi, documento: nome }) };
      }
      return null;
    case 'firma_fatta':
      if ((m = d.match(/^Avete firmato (.+)$/))) {
        const nome = conArticoloDocumento(m[1], lingua);
        return { tipo: 'g', testo: frase(lingua, () => `Avete firmato ${nome}`, 'firma_fatta', { documento: nome }) };
      }
      return null;
    // testi già scritti dal server per il cliente ("Karolina ha spostato la visita a sabato 19 alle 10:30"): restano
    // in italiano (li scrive il backend), con la casa aggiunta solo in italiano
    case 'visita_spostata':
    case 'visita_annullata':
      if (!d) return null;
      return { tipo: evento.tipo_evento === 'visita_spostata' ? 'k' : '',
        testo: immobile && lingua === PRINCIPALE ? `${d.replace(/ la visita\b/, ` la visita ${conArticolo(im, 'a')}`)}` : d };
    case 'prezzo_cambiato':
      if (immobile && (m = d.match(/ è (sceso|salito) a (.+)$/))) {
        // il server scrive "298.000 € (era 315.000 €)": anche "era" va nella lingua del cliente
        const prezzo = lingua === PRINCIPALE ? m[2]
          : m[2].replace(/\(era (.+?)\)/, (_, prima) => contesto(lingua).t('immobili.storia.prezzo_era', { prezzo: prima }));
        return { tipo: m[1] === 'sceso' ? 'g' : '',
          testo: frase(lingua, () => `Il prezzo ${conArticolo(im, 'di')} è ${m[1]} a ${m[2]}`,
            m[1] === 'sceso' ? 'prezzo_sceso' : 'prezzo_salito', { immobile: titolo, prezzo }) };
      }
      return d ? { tipo: '', testo: d } : null;
    case 'aggiornamento_immobile': return d ? { tipo: 'k', testo: d } : null;
    case 'immobile_ripristinato':
      return immobile
        ? { tipo: 'b', testo: frase(lingua, () => `${maiuscola(conArticolo(im))} è tornat${im.f ? 'a' : 'o'} nella vostra lista`, 'ripristinato', { immobile: titolo }) } : null;
    default: return null;
  }
};

// adesso = ora di Roma (adessoRoma): giorno e ora della novità sono quelli di Roma anche con il telefono su un altro
// fuso (prima "Oggi · 03:12" a New York per un fatto delle 09:12)
export const novita = (cronologia, immobili, adesso, quante = 4, lingua = PRINCIPALE) => cronologia
  .map((evento) => ({ evento, data: leggiDataUtc(evento.data_evento) }))
  .filter(({ data }) => data)
  .sort((a, b) => b.data - a.data)
  .map(({ evento, data }) => {
    const voce = testoNovita(evento, immobili, adesso, lingua);
    return voce && { ...voce, id: evento.id, quando: quandoNovita(oraDiRoma(data), adesso, lingua) };
  })
  .filter(Boolean)
  .slice(0, quante);

// ---------------------------------------------------------------- documenti

const FRASI_DOCUMENTI = {
  "carta d'identità": "la carta d'identità",
  'codice fiscale / tessera sanitaria': 'il codice fiscale',
  'buste paga': 'le buste paga',
  'cu (certificazione unica)': 'la CU',
  '730 / modello redditi': 'il 730 o il modello Redditi',
  'estratti conto': "l'estratto conto",
  'lista movimenti': 'la lista movimenti',
  'documento di identità': "il documento d'identità",
  'codice fiscale': 'il codice fiscale',
  'permesso di soggiorno': 'il permesso di soggiorno',
  'buste paga (ultimi 3 mesi)': 'le buste paga degli ultimi 3 mesi',
  cud: 'il CUD',
  'contratto di lavoro': 'il contratto di lavoro',
  'estratto conto bancario': "l'estratto conto",
  'visura camerale': 'la visura camerale',
  'dichiarazione dei redditi (unico)': 'la dichiarazione dei redditi (Unico)',
  'f24 (ultimi pagamenti)': 'gli F24 degli ultimi pagamenti',
  'cedolino pensione': 'il cedolino della pensione',
  'buste paga / dichiarazione redditi': 'le buste paga o la dichiarazione dei redditi',
};
// La categoria del documento → la chiave della frase (home.da_fare.perche.*)
const PERCHE_DOCUMENTO = { identità: 'identita', reddito: 'reddito', finanziario: 'finanziario' };

// Articolo dalla prima parola del nome del documento (o del modulo da firmare); le altre con regole semplici
const ARTICOLI_DOCUMENTO = {
  passaporto: 'il', codice: 'il', permesso: 'il', certificato: 'il', contratto: 'il', modulo: 'il', mandato: 'il', cedolino: 'il',
  documento: 'il', verbale: 'il', preliminare: 'il', compromesso: 'il', 730: 'il', cud: 'il', stato: 'lo', statuto: 'lo',
  atto: "l'", estratto: "l'", incarico: "l'", attestato: "l'", assegno: "l'", ape: "l'", f24: "l'", informativa: "l'", accordo: "l'",
  autorizzazione: "l'", carta: 'la', busta: 'la', dichiarazione: 'la', lista: 'la', planimetria: 'la', visura: 'la', delibera: 'la',
  perizia: 'la', bozza: 'la', copia: 'la', proposta: 'la', privacy: 'la', ricevuta: 'la', certificazione: 'la', cu: 'la', procura: 'la',
  liberatoria: 'la', fattura: 'la', buste: 'le', dichiarazioni: 'le', ricevute: 'le', fatture: 'le', visure: 'le', estratti: 'gli',
  atti: 'gli', contratti: 'i', documenti: 'i', certificati: 'i', moduli: 'i', cedolini: 'i',
};
const articoloDocumento = (parola) => {
  const p = parola.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase(); // senza accenti
  if (ARTICOLI_DOCUMENTO[p]) return ARTICOLI_DOCUMENTO[p];
  if (/^[aeiou]/.test(p)) return "l'";
  if (/^(s[^aeiou]|z|gn|ps|x|y)/.test(p)) return 'lo';
  if (/(a|zione|sione|ta)$/.test(p)) return 'la';
  return 'il';
};

// "Passaporto" → "il passaporto", "Incarico di mediazione" → "l'incarico di mediazione", "CU" → "la CU".
// I nomi dei documenti arrivano dal server in italiano: fuori dall'italiano restano com'è, senza articolo.
export const conArticoloDocumento = (nome, lingua = PRINCIPALE) => {
  const t = String(nome || '').trim();
  if (!t || lingua !== PRINCIPALE) return t;
  const art = articoloDocumento(t.split(/[\s/(]/)[0]);
  const testo = /^[A-Z0-9]{2,}\b/.test(t) ? t : minuscola(t); // le sigle restano maiuscole
  return art.endsWith("'") ? `${art}${testo}` : `${art} ${testo}`;
};

// Il documento come si dice in una frase: "la carta d'identità", "le buste paga", "la carta d'identità di Olena"
export const fraseDocumento = (nome, lingua = PRINCIPALE) => {
  const testo = String(nome || '').trim();
  if (lingua !== PRINCIPALE) return testo;
  const nota = FRASI_DOCUMENTI[testo.toLowerCase()];
  if (nota) return nota;
  const familiare = testo.match(/^(.+) di (\p{Lu}[\p{L}'’-]*)$/u); // il documento di un familiare
  if (familiare) return `${fraseDocumento(familiare[1])} di ${familiare[2]}`;
  return conArticoloDocumento(testo);
};

// Documenti obbligatori ancora da caricare, con la frase e il perché
// (dal catalogo arrivano anche "a cosa serve" e i mesi precisi: "ci servono giugno e agosto 2026")
const maiuscola1 = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);
// "entro il 20 settembre": quanti giorni mancano (le richieste con la data vanno in cima)
const MESI_ENTRO = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
export const conteggioEntro = (entro, adesso = new Date(), lingua = PRINCIPALE) => {
  const { t, tp } = contesto(lingua);
  const [a, m, g] = String(entro || '').slice(0, 10).split('-').map(Number);
  if (!a || !m || !g) return null;
  const data = new Date(a, m - 1, g);
  const giorni = Math.round((data - new Date(adesso.getFullYear(), adesso.getMonth(), adesso.getDate())) / 86400000);
  // con l'anno se la scadenza è di un altro anno (conAnno): prima "entro il 20 gennaio · mancano 126 giorni"
  const quando = lingua === PRINCIPALE ? conAnno(ilGiorno(`${g} ${MESI_ENTRO[m - 1]}`), data, adesso)
    : scrittaConAnno(lingua, data, adesso, { day: 'numeric', month: 'long' });
  if (giorni < 0) return { giorni, testo: t('home.da_fare.era_entro', { quando }) };
  if (giorni === 0) return { giorni, testo: t('home.da_fare.entro_oggi') };
  if (giorni === 1) return { giorni, testo: t('home.da_fare.entro_domani') };
  return { giorni, testo: `${t('home.da_fare.entro', { quando })} · ${tp('home.da_fare.plurali.mancano_giorni', giorni)}` };
};

export const documentiDaFare = (documentiMancanti, adesso = new Date(), lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  return [
    ...(documentiMancanti?.da_firmare || []).map((f) => ({
      nome: f.nome, persona: 'titolare', categoria: 'firma', titolo: t('home.da_fare.firmate', { documento: conArticoloDocumento(f.nome, lingua) }),
      perche: t('home.da_fare.dal_telefono'), link: `/firma/${f.id}`, bottone: t('home.da_fare.firma'),
    })),
    ...(documentiMancanti?.checklist || [])
      .filter((d) => d.obbligatorio && !d.caricato)
      .map((d) => {
        const entro = conteggioEntro(d.entro, adesso, lingua);
        const documento = fraseDocumento(d.nome, lingua);
        // il perché arriva dal server (in italiano) quando c'è; altrimenti è la frase della categoria
        const perche = d.rifiuto ? maiuscola1(d.rifiuto) : d.dettaglio ? maiuscola1(d.dettaglio)
          : maiuscola1(d.a_cosa_serve) || t(`home.da_fare.perche.${PERCHE_DOCUMENTO[d.categoria] || 'altro'}`);
        const perChi = d.persona && d.persona !== 'titolare' && !d.nome.includes(` di ${d.persona.split(' ')[0]}`)
          ? ` · ${t('home.da_fare.per_persona', { persona: d.persona })}` : '';
        return {
          ...d,
          entro: entro || null,
          titolo: t(d.rifiuto ? 'home.da_fare.ricaricate' : 'home.da_fare.caricate', { documento }),
          perche: `${perche}${perChi}`,
        };
      })
      .sort((x, y) => (x.entro ? x.entro.giorni : 9999) - (y.entro ? y.entro.giorni : 9999)),
  ];
};

// Una voce di "Ricevuti" (un tipo per persona) conta come arrivata solo se ha almeno un file non rifiutato: un
// documento rifiutato è ancora da ricaricare (sta tra quelli che mancano). Stessa regola della pagina Documenti.
export const ricevutiValidi = (gruppi) => (gruppi || []).reduce(
  // né rifiutato né scaduto (un documento scaduto va ricaricato, come dice il server)
  (n, g) => n + (g.voci || []).filter((v) => (v.documenti || []).some((d) => d.stato !== 'rifiutato' && d.scadenza_stato !== 'scaduto')).length, 0,
);

// La card Documenti della home conta come la pagina Documenti (GET /documenti/pratica): quelli che mancano
// (le richieste aperte, i periodici e le firme da fare, come il titolo della pagina) e quelli ricevuti validi (uno
// per tipo e persona, anche se non richiesti). Prima le firme non contavano: Lela in home «Ne manca uno», nella
// pagina «Ne mancano 2».
export const riepilogoPratica = (pratica) => {
  const richiesti = pratica?.richiesti || [];
  const mancano = (pratica?.mancano ?? richiesti.length) + (pratica?.da_firmare || []).length;
  const ricevuti = ricevutiValidi(pratica?.ricevuti);
  // la firma da fare viene prima (come in "Da fare"): { nome, firma: true }
  const firma = (pratica?.da_firmare || [])[0];
  return { mancano, ricevuti, totale: mancano + ricevuti, prossimo: firma ? { ...firma, firma: true } : richiesti[0] || null };
};

// ---------------------------------------------------------------- contatti e indicazioni

export const numeroInternazionale = (numero) => {
  let cifre = String(numero || '').replace(/\D/g, '');
  if (cifre.startsWith('00')) cifre = cifre.slice(2);
  else if (cifre.length === 10 && cifre.startsWith('3')) cifre = `39${cifre}`;
  else if (cifre.startsWith('0') && !String(numero).trim().startsWith('+')) cifre = `39${cifre}`; // fisso italiano: 02 1234567
  return cifre.length >= 8 && cifre.length <= 15 ? cifre : null;
};

export const linkWhatsApp = (numero) => {
  const n = numeroInternazionale(numero);
  return n ? `https://wa.me/${n}` : null;
};

export const linkChiama = (numero) => {
  const n = numeroInternazionale(numero);
  return n ? `tel:+${n}` : null;
};

export const indirizzoCompleto = (immobile) => [immobile?.indirizzo, immobile?.citta].filter(Boolean).join(', ');

export const linkIndicazioni = (immobile, luogo) => {
  const dove = luogo || indirizzoCompleto(immobile);
  return dove ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(dove)}` : null;
};

// "Visita al trilocale via Manzoni 14" quando il titolo comincia con il tipo di immobile
const ARTICOLI = [
  [/^(appartamento|attico|open space)\b/i, "all'"], [/^(villa|villetta|casa|mansarda|porzione)\b/i, 'alla '],
  [/^(monolocale|bilocale|trilocale|quadrilocale|loft|rustico|box)\b/i, 'al '],
];
export const titoloVisita = (titolo, lingua = PRINCIPALE) => {
  const t = (titolo || '').split(',')[0].trim(); // "Trilocale via Manzoni 14, Cernusco s/N": la città è già sotto
  // fuori dall'italiano niente articolo ("Осмотр: Trilocale via Manzoni 14"): il caso cambierebbe la parola
  if (lingua !== PRINCIPALE) return contesto(lingua).t('calendario.visita_a', { immobile: t || contesto(lingua).t('immobili.titolo_generico') });
  const trovato = ARTICOLI.find(([re]) => re.test(t));
  return trovato ? `Visita ${trovato[1]}${t.charAt(0).toLowerCase()}${t.slice(1)}` : `Visita: ${t || 'immobile'}`;
};

// ---------------------------------------------------------------- tutto lo stato della home

// Fase Proposta senza nessuna proposta aperta (la visita è piaciuta, l'offerta è da preparare): "la trattativa è
// aperta" non era vero (prima «Jean-Luc, la trattativa è aperta.» senza nessuna offerta mandata)
export const TITOLO_PROPOSTA_DA_PREPARARE = 'prepariamo la proposta.';

// La banca ha rifiutato il mutuo della proposta accettata (decisione del giro 7: la fase resta Mutuo, il rogito solo
// previsto). Stessa regola della card "Il vostro percorso" (SezioniHome).
export const mutuoRifiutato = (proposte = []) => proposte.find((p) => p.stato === 'accettata' && p.mutuo_stato === 'rifiutato') || null;
// Con il mutuo rifiutato "manca poco al rogito", "prossimo passo: il rogito" e "con la delibera, fissiamo il rogito dal
// notaio" non erano più veri: la home li diceva lo stesso mentre la card sotto annunciava il rifiuto della banca.
export const TITOLO_MUTUO_RIFIUTATO = 'vediamo le alternative per il mutuo.';
export const DOPO_MUTUO_RIFIUTATO = "cerchiamo un'altra strada per il mutuo e poi fissiamo il rogito.";
export const PASSO_MUTUO_RIFIUTATO = 'le alternative per il mutuo';

export const faseConTitolo = (fase, proposte = []) => {
  if (fase?.chiave === 'proposta' && !proposte.some((p) => !STATI_PROPOSTA_CHIUSI.includes(p.stato))) {
    return { ...fase, titolo: TITOLO_PROPOSTA_DA_PREPARARE };
  }
  if (fase?.chiave === 'mutuo' && mutuoRifiutato(proposte)) {
    return { ...fase, titolo: TITOLO_MUTUO_RIFIUTATO, dopo: DOPO_MUTUO_RIFIUTATO, prossimoPasso: PASSO_MUTUO_RIFIUTATO };
  }
  return fase;
};

// Le frasi della fase nel dizionario (home.titoli.*, home.dopo_testo.*, home.passi.*): di solito la chiave è quella
// della fase, ma le due situazioni particolari (proposta da preparare, mutuo rifiutato) hanno le loro frasi.
// La si riconosce dal titolo che faseConTitolo ha messo, così la fase resta l'oggetto di sempre.
// quale: 'titolo' (anche la proposta da preparare ha il suo) oppure 'dopo' e 'passo' (solo il mutuo rifiutato).
export const chiaveFrasiFase = (fase, quale = 'titolo') => {
  if (fase?.titolo === TITOLO_MUTUO_RIFIUTATO) return 'mutuo_rifiutato';
  if (quale === 'titolo' && fase?.titolo === TITOLO_PROPOSTA_DA_PREPARARE) return 'proposta_da_preparare';
  return fase?.chiave;
};

export const statoHome = ({ cliente, immobili = [], visite = [], proposte = [], cronologia = [], documentiMancanti = null, pratica = null }, adesso, lingua = PRINCIPALE) => {
  const oggi = appuntamentiDiOggi(visite, proposte, immobili, adesso);
  const esiti = esitiInSospeso(visite, immobili, adesso);
  const prossimo = prossimoAppuntamento(visite, proposte, immobili, adesso);
  const fase = faseConTitolo(FASI[faseDelPercorso(visite, proposte, immobili, adesso) - 1], proposte);
  const daFare = documentiDaFare(documentiMancanti, adesso, lingua);
  return {
    fase,
    oggi,
    esiti,
    prossimo,
    daFare,
    documenti: riepilogoPratica(pratica),
    // le case in gioco, come "7 case" in /immobili (le scartate non contano)
    inGioco: immobili.filter((i) => etichettaImmobile(i, visite, proposte, adesso).classe !== 'scartato').length,
    recenti: immobiliRecenti(immobili, visite, proposte, adesso, 3, lingua),
    novita: novita(cronologia, immobili, adesso, 4, lingua),
    riepilogo: riepilogo({ oggi, esiti, proposte, daFare, immobili, prossimo, adesso, lingua }),
    ricerca: {
      ...(({ budget, etichetta }) => ({ budget, budgetEtichetta: etichetta }))(budgetRicerca(cliente, lingua)),
      zone: zoneRicerca(cliente),
      cosa: cosaCercate(cliente, lingua),
      avere: daAvere(cliente, lingua),
      conNoi: conNoiDal(cliente?.data_registrazione, adesso, lingua),
    },
  };
};

// ---------------------------------------------------------------- mutuo: i passi della banca

// Nella fase Mutuo la card "Il vostro percorso" mostra istruttoria → perizia → delibera → erogazione, dai campi della
// proposta accettata che lo staff aggiorna (mutuo_stato e le date).
const PASSI_MUTUO = [
  { chiave: 'istruttoria', nome: 'Istruttoria', data: 'mutuo_data_richiesta', stati: ['richiesto', 'perizia', 'delibera', 'erogato'] },
  { chiave: 'perizia', nome: 'Perizia', data: 'mutuo_data_perizia', stati: ['delibera', 'erogato'] },
  { chiave: 'delibera', nome: 'Delibera', data: 'mutuo_data_delibera', stati: ['delibera', 'erogato'] },
  { chiave: 'erogazione', nome: 'Erogazione', data: 'mutuo_data_erogazione', stati: ['erogato'] },
];
// il nome del passo nella lingua del cliente (home.mutuo.passi.*)
export const nomePassoMutuo = (chiave, lingua = PRINCIPALE) => contesto(lingua).t(`home.mutuo.passi.${chiave}`);
export const passiMutuo = (proposte, adesso = new Date()) => {
  const p = (proposte || []).find((x) => x.stato === 'accettata' && (x.mutuo_richiesto || x.mutuo_stato || x.mutuo_banca));
  if (!p || p.mutuo_stato === 'rifiutato') return null;
  let attuale = null;
  const passi = PASSI_MUTUO.map((passo) => {
    const data = p[passo.data] ? leggiData(p[passo.data]) : null;
    const fatto = passo.stati.includes(p.mutuo_stato) || (passo.chiave === 'perizia' && data && data <= adesso && p.mutuo_stato === 'perizia');
    return { ...passo, data, fatto };
  }).map((passo) => {
    if (!passo.fatto && !attuale) { attuale = passo.chiave; return { ...passo, stato: 'ora' }; }
    return { ...passo, stato: passo.fatto ? 'fatto' : 'poi' };
  });
  return { banca: p.mutuo_banca || null, passi };
};
